// Heart-rate zones (#94), on heart-rate reserve (Karvonen):
//   bpm = resting + (max - resting) x percentage
// Five zones at 50/60/70/80/90% of reserve, so Zone 2 (easy aerobic, the
// Zone 2 the planner prescribes) is 60-70%. Reserve fits individuals better
// than % of max because it accounts for resting heart rate.
//
// Max and resting heart rate come from the Profile; when either is missing it
// is estimated: max from age with Tanaka's formula (208 - 0.7 x age, more
// accurate than 220 - age), resting as 60 bpm.

export const DEFAULT_RESTING_HR = 60;
export const ZONE_FLOORS = [0.5, 0.6, 0.7, 0.8, 0.9]; // Zones 1-5, % of reserve
export const ZONE_NAMES = ["Zone 1", "Zone 2", "Zone 3", "Zone 4", "Zone 5"];

// How zones are worked out, chosen in Profile:
//   reserve  % of heart-rate reserve above resting (Karvonen), the default
//   max      % of max heart rate alone (the simpler watch default)
export const ZONE_METHODS = ["reserve", "max"];

// One colour per zone, used wherever zones show (#194 follow-up): the usual
// sports-watch scheme, cool to hot. Index 0 is below Zone 1.
export const ZONE_COLOURS = ["#6b7280", "#9ca3af", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444"];
export const zoneColour = (zone) => ZONE_COLOURS[Math.max(0, Math.min(5, zone || 0))];

/** The colour for a heart rate, given zone floors from zoneFloors(). */
export const zoneColourFor = (bpm, floors) => {
  if (!Array.isArray(floors) || !(bpm > 0)) return ZONE_COLOURS[0];
  let zone = 0;
  floors.forEach((floor, i) => {
    if (bpm >= floor) zone = i + 1;
  });
  return ZONE_COLOURS[zone];
};

/** Tanaka's age-predicted maximum heart rate, or null without an age. */
export const estimateMaxHr = (birthYear, now = new Date()) => {
  const year = Number(birthYear);
  if (!Number.isInteger(year) || year < 1900) return null;
  const age = new Date(now).getFullYear() - year;
  if (age < 10 || age > 100) return null;
  return Math.round(208 - 0.7 * age);
};

/**
 * The heart rates zones are worked out from, and which are estimates.
 * Null when there is no max heart rate to go on (no entry and no age).
 *
 * @returns {null | { max, resting, maxEstimated, restingEstimated }}
 */
export const heartRateBasis = (profile, now = new Date()) => {
  // Accepts the app's profile (camelCase) or the API's (snake_case; the
  // birth year lives in its `age` column).
  const enteredMax = Number(profile?.maxHeartRate ?? profile?.max_heart_rate) || null;
  const enteredResting = Number(profile?.restingHeartRate ?? profile?.resting_heart_rate) || null;
  const birthYear = profile?.birthYear ?? profile?.birth_year ?? profile?.age;
  const max = enteredMax || estimateMaxHr(birthYear, now);
  if (!max) return null;
  const resting = enteredResting || DEFAULT_RESTING_HR;
  if (resting >= max - 20) return null; // not enough reserve to split into zones
  // Body weight rides along for the VO2max estimate (#194).
  const weightKg = Number(profile?.bodyWeightKg ?? profile?.body_weight_kg) || null;
  const method = (profile?.hrZoneMethod ?? profile?.hr_zone_method) === "max" ? "max" : "reserve";
  return { max, resting, maxEstimated: !enteredMax, restingEstimated: !enteredResting, weightKg, method };
};

/** The bpm at which each zone starts: [Z1, Z2, Z3, Z4, Z5]. */
export const zoneFloors = ({ max, resting, method = "reserve" }) =>
  ZONE_FLOORS.map((pct) =>
    Math.round(method === "max" ? max * pct : resting + (max - resting) * pct),
  );

/** 0 below Zone 1, otherwise the zone number 1-5. */
export const zoneOf = (bpm, basis) => {
  const floors = zoneFloors(basis);
  let zone = 0;
  floors.forEach((floor, i) => {
    if (bpm >= floor) zone = i + 1;
  });
  return zone;
};

/**
 * Seconds in each zone from heart-rate samples, each worth `secondsEach`.
 * Index 0 is time below Zone 1; 1-5 are the zones.
 *
 * @param {number[]} bpms
 * @returns {number[]} length 6
 */
export const timeInZones = (bpms, basis, secondsEach = 1) => {
  const out = [0, 0, 0, 0, 0, 0];
  if (!basis) return out;
  for (const bpm of bpms) {
    const n = Number(bpm);
    if (n > 0) out[zoneOf(n, basis)] += secondsEach;
  }
  return out;
};

/**
 * The zone summary saved with a session: seconds per zone plus the heart
 * rates it was worked out from, so a later change to the Profile doesn't
 * quietly rewrite old sessions. Null without a basis or any samples.
 */
export const zoneSummary = (bpms, basis, secondsEach = 1) => {
  if (!basis || !bpms?.length) return null;
  return {
    seconds: timeInZones(bpms, basis, secondsEach),
    max: basis.max,
    resting: basis.resting,
    method: basis.method || "reserve",
  };
};

/** Minutes in Zone 2 from a saved zone summary (0 without one). */
export const zone2Minutes = (zones) => Math.round((zones?.seconds?.[2] || 0) / 60);

// Raising the max from what a session recorded (#187). A max heart rate set
// too low pushes every zone down, and the age formula is often off by 10 bpm
// or more, so a strap reading above it is better evidence. One-beat spikes are
// common strap artefacts (a dry contact, a static jolt), so the peak must be
// held for PEAK_HOLD_SEC seconds in a row.
export const PEAK_HOLD_SEC = 5;
export const PLAUSIBLE_MAX_HR = 230;

/**
 * The highest heart rate held for `holdSec` consecutive 1 Hz samples, or null.
 * Samples are telemetry rows (`{ t, hr? }`); a gap in `t` or a missing `hr`
 * breaks the run.
 */
export const sustainedPeakHr = (samples, holdSec = PEAK_HOLD_SEC) => {
  if (!Array.isArray(samples)) return null;
  let best = null;
  let run = []; // bpms of the current unbroken run
  let lastT = null;
  for (const row of samples) {
    const bpm = Number(row?.hr);
    const contiguous = lastT !== null && row?.t === lastT + 1;
    if (!(bpm > 0)) {
      run = [];
      lastT = row?.t ?? null;
      continue;
    }
    if (!contiguous) run = [];
    run.push(bpm);
    if (run.length > holdSec) run.shift();
    if (run.length === holdSec) {
      const floor = Math.min(...run);
      if (best === null || floor > best) best = floor;
    }
    lastT = row?.t ?? null;
  }
  return best;
};

/**
 * The new max heart rate when `peak` beats the one zones use now, else null.
 * Without a basis there is nothing to beat (a session peak alone is no max).
 */
export const raisedMaxHr = (peak, basis) => {
  const bpm = Math.round(Number(peak));
  if (!basis || !(bpm > basis.max) || bpm > PLAUSIBLE_MAX_HR) return null;
  return bpm;
};
